import { useState, useCallback, useEffect, useRef } from 'react';

export const useCodeExplanation = () => {
  const [explanations, setExplanations] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [fullExplanation, setFullExplanation] = useState(null);
  const [debugAnalysis, setDebugAnalysis] = useState(null);
  const [optimization, setOptimization] = useState(null);
  const [quiz, setQuiz] = useState(null);
  const [suggestion, setSuggestion] = useState(null);

  // Get line-by-line explanations for code
  const getLineExplanations = useCallback(async (code) => {
    if (!code.trim()) return {};
    
    setIsLoading(true);
    
    try {
      const response = await fetch('/api/explain', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      });
      
      if (response.ok) {
        const data = await response.json();
        setExplanations(data.explanations || {});
        return data.explanations || {};
      }
      // Fallback when response not ok
      const mockExplanations = generateMockExplanations(code);
      setExplanations(mockExplanations);
      return mockExplanations;
    } catch (err) {
      console.error('Error getting explanations:', err);
      const mockExplanations = generateMockExplanations(code);
      setExplanations(mockExplanations);
      return mockExplanations;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Get full program explanation
  const getFullExplanation = useCallback(async (code) => {
    if (!code.trim()) return null;
    
    setIsLoading(true);
    
    try {
      const response = await fetch('/api/explain/full', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      });
      
      if (response.ok) {
        const data = await response.json();
        setFullExplanation(data.explanation);
        setIsLoading(false);
        return data.explanation;
      }
    } catch (err) {
      console.error('Error getting full explanation:', err);
    }
    
    // Fallback mock
    const mock = generateMockFullExplanation(code);
    setFullExplanation(mock);
    setIsLoading(false);
    return mock;
  }, []);

  // Get debug/error analysis
  const getDebugAnalysis = useCallback(async (code, error) => {
    if (!code.trim()) return null;

    setIsLoading(true);

    try {
      const response = await fetch('/api/explain/debug', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, error }),
      });

      if (response.ok) {
        const data = await response.json();
        setDebugAnalysis(data.analysis);
        return data.analysis;
      }
    } catch (err) {
      console.error('Error getting debug analysis:', err);
    }

    const fallback = `## ❌ ניתוח שגיאה\n\n**שגיאה:** ${error || 'לא ידועה'}\n\nבדוק את הקוד ונסה שוב.`;
    setDebugAnalysis(fallback);
    setIsLoading(false);
    return fallback;
  }, []);

  // Get code optimization suggestions
  const getOptimization = useCallback(async (code) => {
    if (!code.trim()) return null;

    setIsLoading(true);

    try {
      const response = await fetch('/api/explain/optimize', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      });

      if (response.ok) {
        const data = await response.json();
        setOptimization(data.optimization);
        return data.optimization;
      }
    } catch (err) {
      console.error('Error getting optimization:', err);
    }

    setOptimization(null);
    setIsLoading(false);
    return null;
  }, []);

  // Get a quiz question based on the code
  const getQuiz = useCallback(async (code) => {
    if (!code.trim()) return null;

    setIsLoading(true);

    try {
      const response = await fetch('/api/explain/quiz', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      });

      if (response.ok) {
        const data = await response.json();
        setQuiz(data.quiz);
        return data.quiz;
      }
    } catch (err) {
      console.error('Error getting quiz:', err);
    }

    setQuiz(null);
    setIsLoading(false);
    return null;
  }, []);

  // Get a real-time suggestion/hint
  const getRealTimeSuggestion = useCallback(async (code) => {
    if (!code || code.trim().length < 20) {
      setSuggestion(null);
      return null;
    }

    try {
      const response = await fetch('/api/suggest', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      });

      if (response.ok) {
        const data = await response.json();
        setSuggestion(data.suggestion);
        return data.suggestion;
      }
    } catch (err) {
      // Silently fail for suggestions - they're non-critical
    }

    return null;
  }, []);

  // Clear explanations
  const clearExplanations = useCallback(() => {
    setExplanations({});
    setFullExplanation(null);
    setDebugAnalysis(null);
    setOptimization(null);
    setQuiz(null);
  }, []);

  return {
    explanations,
    fullExplanation,
    debugAnalysis,
    optimization,
    quiz,
    suggestion,
    isLoading,
    getLineExplanations,
    getFullExplanation,
    getDebugAnalysis,
    getOptimization,
    getQuiz,
    getRealTimeSuggestion,
    clearExplanations,
  };
};

/**
 * Hook that provides debounced real-time suggestions.
 * Call this from the App component and pass the current code.
 */
export const useRealTimeSuggestions = (code, isDebugging) => {
  const [suggestion, setSuggestion] = useState(null);
  const timerRef = useRef(null);

  useEffect(() => {
    // Clear previous timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    // Don't fetch during debug or for short code
    if (isDebugging || !code || code.trim().length < 20) {
      setSuggestion(null);
      return;
    }

    // Debounce: wait 12 seconds after last code change
    timerRef.current = setTimeout(async () => {
      try {
        const response = await fetch('/api/suggest', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code }),
        });

        if (response.ok) {
          const data = await response.json();
          setSuggestion(data.suggestion);
        }
      } catch {
        // Silently fail
      }
    }, 12000);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [code, isDebugging]);

  return suggestion;
};

// Generate mock line explanations locally
function generateMockExplanations(code) {
  const lines = code.split('\n');
  const explanations = {};
  
  lines.forEach((line, index) => {
    const lineNum = index + 1;
    const trimmed = line.trim();
    
    if (!trimmed || trimmed.startsWith('#')) {
      return;
    }
    
    // Pattern matching for common Python constructs
    if (trimmed.match(/^(\w+)\s*=\s*input\(/)) {
      const varName = trimmed.match(/^(\w+)/)[1];
      explanations[lineNum] = `🎤 מבקשים קלט מהמשתמש ושומרים אותו במשתנה ${varName}`;
    } else if (trimmed.match(/^(\w+)\s*=\s*\d+/)) {
      const varName = trimmed.match(/^(\w+)/)[1];
      const value = trimmed.match(/=\s*(\d+)/)[1];
      explanations[lineNum] = `📦 יוצרים משתנה ${varName} ושומרים בו את המספר ${value}`;
    } else if (trimmed.match(/^(\w+)\s*=\s*["']/)) {
      const varName = trimmed.match(/^(\w+)/)[1];
      explanations[lineNum] = `📝 יוצרים משתנה ${varName} ושומרים בו טקסט (מחרוזת)`;
    } else if (trimmed.match(/^(\w+)\s*=\s*\[/)) {
      const varName = trimmed.match(/^(\w+)/)[1];
      explanations[lineNum] = `📋 יוצרים רשימה (list) ושומרים אותה במשתנה ${varName}`;
    } else if (trimmed.startsWith('print(')) {
      explanations[lineNum] = `🖨️ מדפיסים לפלט (מציגים על המסך)`;
    } else if (trimmed.startsWith('for ')) {
      if (trimmed.includes('range(')) {
        const match = trimmed.match(/for\s+(\w+)\s+in\s+range\((\d+)/);
        if (match) {
          explanations[lineNum] = `🔄 לולאה שרצה ${match[2]} פעמים, בכל פעם ${match[1]} מקבל ערך אחר`;
        } else {
          explanations[lineNum] = `🔄 לולאת for - חוזרת על פעולה מספר פעמים`;
        }
      } else {
        explanations[lineNum] = `🔄 לולאת for - עוברת על כל פריט ברשימה`;
      }
    } else if (trimmed.startsWith('while ')) {
      explanations[lineNum] = `🔄 לולאת while - ממשיכה כל עוד התנאי נכון`;
    } else if (trimmed.startsWith('if ')) {
      explanations[lineNum] = `❓ בדיקת תנאי - אם התנאי נכון, מבצעים את הקוד`;
    } else if (trimmed.startsWith('elif ')) {
      explanations[lineNum] = `❓ תנאי נוסף - אם התנאי הקודם לא נכון, בודקים תנאי זה`;
    } else if (trimmed.startsWith('else:')) {
      explanations[lineNum] = `❓ אחרת - מבצעים אם אף תנאי לא התקיים`;
    } else if (trimmed.startsWith('def ')) {
      const funcName = trimmed.match(/def\s+(\w+)/)?.[1];
      explanations[lineNum] = `🔧 מגדירים פונקציה בשם ${funcName || '?'} - בלוק קוד שאפשר להשתמש בו שוב`;
    } else if (trimmed.startsWith('return ')) {
      explanations[lineNum] = `↩️ מחזירים ערך מהפונקציה`;
    } else if (trimmed.match(/^\w+\s*\+=/)) {
      explanations[lineNum] = `➕ מוסיפים ערך למשתנה`;
    } else if (trimmed.match(/^\w+\s*-=/)) {
      explanations[lineNum] = `➖ מחסירים ערך מהמשתנה`;
    } else if (trimmed.match(/^\w+\s*\*=/)) {
      explanations[lineNum] = `✖️ מכפילים את המשתנה`;
    } else if (trimmed.match(/^(\w+)\s*=\s*(\w+)\s*\+/)) {
      explanations[lineNum] = `🔢 מחשבים חיבור ושומרים בתוצאה`;
    } else if (trimmed.match(/^(\w+)\s*=\s*(\w+)\s*\*/)) {
      explanations[lineNum] = `🔢 מחשבים כפל ושומרים את התוצאה`;
    } else if (trimmed.match(/^\w+\s*=/)) {
      const varName = trimmed.match(/^(\w+)/)[1];
      explanations[lineNum] = `📦 שומרים ערך במשתנה ${varName}`;
    } else if (trimmed.match(/^\w+\(/)) {
      const funcName = trimmed.match(/^(\w+)/)[1];
      explanations[lineNum] = `⚡ קוראים לפונקציה ${funcName}`;
    }
  });
  
  return explanations;
}

// Generate mock full explanation
function generateMockFullExplanation(code) {
  const hasLoop = code.includes('for ') || code.includes('while ');
  const hasCondition = code.includes('if ');
  const hasFunction = code.includes('def ');
  const hasInput = code.includes('input(');
  const hasPrint = code.includes('print(');
  
  let explanation = '## 📖 הסבר התוכנית\n\n';
  
  if (hasFunction) {
    explanation += '### 🔧 פונקציות\nהקוד מכיל הגדרת פונקציה - זה כמו "מתכון" שאפשר להשתמש בו שוב ושוב.\n\n';
  }
  
  if (hasInput) {
    explanation += '### 🎤 קלט\nהתוכנית מבקשת קלט מהמשתמש באמצעות `input()`. הערך שהמשתמש מקליד נשמר במשתנה.\n\n';
  }
  
  if (hasLoop) {
    explanation += '### 🔄 לולאות\nהקוד משתמש בלולאה - זה מאפשר לחזור על פעולה מספר פעמים בלי לכתוב את אותו קוד שוב ושוב.\n\n';
  }
  
  if (hasCondition) {
    explanation += '### ❓ תנאים\nיש בקוד בדיקת תנאי (if) - זה מאפשר לתוכנית "להחליט" מה לעשות בהתאם למצב.\n\n';
  }
  
  if (hasPrint) {
    explanation += '### 🖨️ פלט\nהתוכנית מדפיסה מידע למסך באמצעות `print()`. ככה אנחנו יכולים לראות תוצאות.\n\n';
  }
  
  explanation += '### 💡 טיפ\nנסה לשנות את הערכים בקוד ולהריץ שוב - ככה תבין טוב יותר איך כל חלק עובד!';
  
  return explanation;
}
